import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import { BdStateStore } from '@bindoc-projects/geographic-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const BD_STATE_IDS_CONDITION_ID = 'stateId';
export const FilterConditionStateIdPluginType: FilterConditionPluginSpecification<
  number[],
  FilterConditionStateIdDto,
  BdStateIdsCondition
> = {
  type: 'stateIds',
  category: 'person',
  displayName: { de: 'Bundesländer' },
  sortingIndex: 1,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionStateIdDto,
  ): FilterConditionPluginImpl<number[]> {
    return new FilterConditionStateIdPlugin(obj.stateIds);
  },
  fromCondition(
    jsonObj: BdStateIdsCondition,
  ): FilterConditionPluginImpl<number[]> {
    return new FilterConditionStateIdPlugin(jsonObj.includes ?? []);
  },
  newInstance(): FilterConditionPluginImpl<number[]> {
    return new FilterConditionStateIdPlugin([]);
  },
};

export class FilterConditionStateIdPlugin extends FilterConditionPluginImpl<
  number[],
  FilterConditionStateIdDto
> {
  static override Specification: FilterConditionPluginSpecification<
    number[],
    FilterConditionStateIdDto,
    BdStateIdsCondition
  > = FilterConditionStateIdPluginType;

  constructor(private stateIds: number[]) {
    super(FilterConditionStateIdPluginType.type);
  }

  clone(): FilterConditionStateIdPlugin {
    return new FilterConditionStateIdPlugin([...(this.stateIds ?? [])]);
  }

  override equals(other: FilterConditionStateIdPlugin): boolean {
    return (
      super.equals(other) && BdArrayUtil.equals(this.stateIds, other.stateIds)
    );
  }

  override validationToText(): string {
    if (this.stateIds.length === 0) {
      return 'Mindestens ein Bundesland auswählen';
    } else {
      return '';
    }
  }

  toCondition(): BdCondition | null {
    return new BdStateIdsCondition(this.stateIds, []);
  }

  toJSON(): FilterConditionStateIdDto | null {
    return {
      type: this.type,
      stateIds: this.stateIds,
    };
  }

  toText(): string | null {
    return this.stateIds.length === 1
      ? 'ein Bundesland'
      : `${this.stateIds.length} Bundesländer`;
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items = this.stateIds.map((id) => {
      const state = BdStateStore.byId(id);
      return state ? state.name : String(id);
    });
    return { title, items };
  }

  toValue(): number[] {
    return this.stateIds;
  }
}

export class BdStateIdsCondition extends BdContentCondition<number> {
  constructor(includes: number[], excludes: number[]) {
    super(BD_STATE_IDS_CONDITION_ID, includes, excludes);
  }
}

interface FilterConditionStateIdDto {
  type: string;
  stateIds: number[];
}
